import { Reveal, SectionLabel } from "./Reveal";

const AskMock = () => (
  <div className="hard-shadow border-2 border-black bg-white" data-testid="ask-mockup">
    <div className="flex items-center justify-between border-b-2 border-black px-5 py-3">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/50">Qwiri — Ask</span>
      <span className="flex gap-1.5">
        <span className="h-2 w-2 border border-black" />
        <span className="h-2 w-2 border border-black" />
        <span className="h-2 w-2 bg-black" />
      </span>
    </div>
    <div className="space-y-4 p-5 md:p-6">
      <div className="ml-auto w-fit max-w-[85%] border-2 border-black bg-black px-4 py-3 text-sm font-semibold text-white">
        What did we commit to Wells Fargo — and is it built?
      </div>
      <div className="w-fit max-w-[95%] border-2 border-black px-4 py-4">
        <p className="text-sm leading-relaxed">
          A proposal including the <span className="font-bold">cortex integration</span> was sent on
          11 Aug. The repo's last commit was <span className="font-bold">10 Aug, 09:04</span> — no
          build activity since. The client demo is confirmed for 27 Aug.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {["HubSpot · Deal #4821 · 11 Aug 14:20", "GitHub · cortex@main · 10 Aug 09:04", "Calendar · Demo · 27 Aug 11:00"].map((c) => (
            <span key={c} className="border border-black px-2 py-1 text-[10px] font-bold uppercase tracking-wider">
              {c}
            </span>
          ))}
        </div>
        <p className="mt-3 border-t border-black/15 pt-3 text-[11px] font-semibold uppercase tracking-wider text-black/50">
          Unconfirmed link: repo ↔ account is inferred — flagged, not hidden
        </p>
      </div>
    </div>
  </div>
);

const DriftMock = () => (
  <div className="hard-shadow-white border-2 border-white bg-black text-white" data-testid="drift-mockup">
    <div className="flex items-center justify-between border-b-2 border-white px-5 py-3">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Drift detected</span>
      <span className="bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-black">High confidence</span>
    </div>
    <div className="p-5 md:p-6">
      <h4 className="text-lg font-extrabold tracking-tight md:text-xl">Commitment gap — Wells Fargo</h4>
      <div className="mt-4 divide-y divide-white/15 border border-white/25">
        {[
          ["CRM", "Deal moved to Proposal Sent", "11 Aug · 14:20"],
          ["Repo", "No commits on cortex", "11 days idle"],
          ["Calendar", "Client technical demo", "27 Aug · 11:00"],
        ].map(([s, r, t]) => (
          <div key={s} className="flex items-baseline justify-between gap-3 px-4 py-3 text-sm">
            <span className="w-16 shrink-0 text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">{s}</span>
            <span className="flex-1 font-semibold">{r}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">{t}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <span className="border border-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest">Confirm</span>
        <span className="border border-white/40 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white/60">Dismiss</span>
        <span className="bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-black">Open in Ask</span>
      </div>
    </div>
  </div>
);

const PulseMock = () => (
  <div className="hard-shadow border-2 border-black bg-white" data-testid="pulse-mockup">
    <div className="flex items-center justify-between border-b-2 border-black px-5 py-3">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/50">Pulse — Week 33</span>
      <div className="flex gap-1 text-[9px] font-bold uppercase tracking-widest">
        <span className="bg-black px-2 py-1 text-white">Exec</span>
        <span className="border border-black px-2 py-1">Eng lead</span>
      </div>
    </div>
    <div className="divide-y divide-black/15 p-0">
      {[
        ["Shipped", "3 features translated from commits into outcomes — incl. SSO for enterprise tier."],
        ["Moved", "2 deals advanced. Wells Fargo to Proposal Sent; Batelco integration scoped."],
        ["At risk", "1 commitment gap (demo 27 Aug, repo idle 11 days). 1 ownership vacuum on billing svc."],
      ].map(([t, b]) => (
        <div key={t} className="grid grid-cols-[86px_1fr] gap-3 px-5 py-4 md:px-6">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-black/50">{t}</span>
          <span className="text-sm leading-relaxed">{b}</span>
        </div>
      ))}
      <div className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 md:px-6">
        Generated, not written · Every line cited · Segmented by role
      </div>
    </div>
  </div>
);

const Capability = ({ index, name, headline, body, bullets, mock, flip, dark }) => (
  <div className={`grid items-center gap-10 border-t-2 py-16 md:py-24 lg:grid-cols-2 lg:gap-16 ${dark ? "border-white/20" : "border-black"}`}>
    <Reveal className={flip ? "lg:order-2" : ""}>
      <div className={`text-[11px] font-bold uppercase tracking-[0.2em] ${dark ? "text-white/50" : "text-black/50"}`}>
        Capability {index} — {name}
      </div>
      <h3 className="mt-4 text-[28px] font-bold leading-tight tracking-tight md:text-[40px]">{headline}</h3>
      <p className={`mt-5 max-w-lg text-base leading-relaxed md:text-lg ${dark ? "text-white/65" : "text-black/70"}`}>{body}</p>
      <ul className="mt-6 space-y-3">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-3 text-sm font-semibold md:text-base">
            <span className={`mt-[7px] inline-block h-2 w-2 shrink-0 ${dark ? "bg-white" : "bg-black"}`} />
            {b}
          </li>
        ))}
      </ul>
    </Reveal>
    <Reveal delay={0.15} className={flip ? "lg:order-1" : ""}>
      {mock}
    </Reveal>
  </div>
);

export const Capabilities = () => (
  <section id="product" data-testid="capabilities-section" className="bg-white">
    <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36">
      <SectionLabel index="03" title="The product" />
      <Reveal className="mt-10">
        <h2 className="max-w-4xl text-[28px] font-bold leading-tight tracking-tight md:text-[40px]">
          Three surfaces. <span className="text-black/40">One organization graph underneath.</span>
        </h2>
      </Reveal>

      <div className="mt-8">
        <Capability
          index="01"
          name="Ask"
          headline="Ask anything. Get what's true."
          body="Natural language across every connected source — code, commerce, calendar, conversation. Not links to read: the answer, with the reasoning attached."
          bullets={[
            "Every answer cited with source, timestamp and link",
            "Uncertainty stated in one line — never smoothed over",
            "Permission-scoped invisibly: two people, same question, different correct answers",
          ]}
          mock={<AskMock />}
        />
      </div>

      <div className="bg-black text-white -mx-5 px-5 md:-mx-10 md:px-10">
        <Capability
          dark
          flip
          index="02"
          name="Drift"
          headline="Your organization notices its own contradictions."
          body="Continuous detection across sources, before the gap surfaces at a client. Five classes at launch — each card shows the evidence, the confidence, and three actions."
          bullets={[
            "Commitment gap · Stale momentum · Duplicate effort",
            "Ownership vacuum · Silent scope change",
            "Confirm, dismiss or open in Ask — every decision teaches the graph",
          ]}
          mock={<DriftMock />}
        />
      </div>

      <Capability
        index="03"
        name="Pulse"
        headline="Every Monday, this writes itself."
        body="The organizational briefing, generated rather than written. What shipped — translated from commits into outcomes. What moved commercially. What is at risk. Segmented by role."
        bullets={[
          "An exec and an engineering lead get different resolutions of the same week",
          "Every line citable back to the record it came from",
          "Always current — rendered from the graph on read, never a stale snapshot",
        ]}
        mock={<PulseMock />}
      />
    </div>
  </section>
);
